import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Link, useSearchParams } from 'react-router-dom';
import SessaoEditModal from './SessaoEditModal';
import './AdminPanel.css';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const EMPTY_FORM = {
  nome: '', tema: '', tipo: 'Apresentação de Temas Livres', categoria: 'temas_livres',
  dia: '', horario_inicio: '', horario_fim: '',
};

const CATEGORIA_LABEL = {
  temas_livres: '📋 Temas Livres',
  posteres: '🖼️ Pôsteres',
  temas_correlatos: '🎤 Temas Correlatos',
};

function AdminPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoriaInicial = Object.prototype.hasOwnProperty.call(CATEGORIA_LABEL, searchParams.get('categoria'))
    ? searchParams.get('categoria')
    : 'temas_livres';
  const [sessoes, setSessoes] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingSessao, setEditingSessao] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [categoriaAtiva, setCategoriaAtiva] = useState(categoriaInicial);
  const [diaAtivo, setDiaAtivo] = useState(searchParams.get('dia') || '');
  const auth = useAuth();
  const csrfHeaders = { headers: { 'X-CSRFToken': getCookie('csrftoken') } };
  const [importando, setImportando] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleImportar = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportando(true);
    const formData = new FormData();
    formData.append('arquivo', file);
    axios.post('/api/importar-apresentacoes/', formData, {
      headers: { 'X-CSRFToken': getCookie('csrftoken'), 'Content-Type': 'multipart/form-data' }
    })
      .then(r => {
        toast.success(r.data.mensagem);
        if (r.data.erros && r.data.erros.length > 0) {
          r.data.erros.forEach(err => toast.error(err));
        }
        fetchSessoes();
      })
      .catch(() => toast.error('Erro ao importar arquivo.'))
      .finally(() => { setImportando(false); e.target.value = ''; });
  };

  const fetchSessoes = () => {
    axios.get('/api/sessoes/')
      .then(r => setSessoes(r.data))
      .catch(() => toast.error('Não foi possível carregar as sessões.'));
  };

  useEffect(() => { fetchSessoes(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    axios.post('/api/sessoes/', formData, csrfHeaders)
      .then(() => {
        toast.success('Sessão criada com sucesso!');
        setFormData(EMPTY_FORM);
        setShowForm(false);
        fetchSessoes();
      })
      .catch(() => toast.error('Erro ao criar a sessão. Verifique os dados.'));
  };

  const handleDelete = (sessao) => {
    axios.delete(`/api/sessoes/${sessao.id}/`, csrfHeaders)
      .then(() => {
        toast.success('Sessão apagada com sucesso!');
        setConfirmDelete(null);
        fetchSessoes();
      })
      .catch(() => toast.error('Erro ao apagar a sessão.'));
  };

  const isAdminPremio = auth.user?.is_admin_premio && !auth.user?.is_staff && !auth.user?.is_superuser;

  const sessoesDaCategoria = sessoes.filter(s => s.categoria === categoriaAtiva);
  const diasDaCategoria = [...new Set(sessoesDaCategoria.map(s => s.dia))].sort();

  useEffect(() => {
    if (!diasDaCategoria.length) return;
    setDiaAtivo(atual => diasDaCategoria.includes(atual) ? atual : (diasDaCategoria[0] || ''));
  }, [categoriaAtiva, sessoes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!diaAtivo) return;
    setSearchParams({ categoria: categoriaAtiva, dia: diaAtivo }, { replace: true });
  }, [categoriaAtiva, diaAtivo, setSearchParams]);

  const filtered = sessoesDaCategoria.filter(s => s.dia === diaAtivo && (
    s.nome.toLowerCase().includes(search.toLowerCase()) ||
    s.tema.toLowerCase().includes(search.toLowerCase())
  ));

  const grouped = filtered.reduce((acc, s) => {
    if (!acc[s.dia]) acc[s.dia] = [];
    acc[s.dia].push(s);
    return acc;
  }, {});

  const formatDate = (d) => new Date(d).toLocaleDateString('pt-BR', {
    timeZone: 'UTC', weekday: 'long', day: '2-digit', month: 'long'
  });

  if (!auth.user?.is_staff && !auth.user?.is_superuser && !auth.user?.is_coordenador && !auth.user?.is_admin_premio) {
    return (
      <div className="admin-container">
        <div className="ap-access-denied">
          <span>🔒</span><h2>Acesso Negado</h2>
          <p>Você não tem permissão para acessar esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="ap-page-header">
        <div className="ap-header-identity">
          <span className="ap-header-icon">▦</span>
          <div>
          <span className="ap-header-eyebrow">CBACV 2026 · Gestão do evento</span>
          <h1 className="ap-page-title">
            {isAdminPremio ? 'Avaliação e Premiação' : 'Painel de Administração'}
          </h1>
          <p className="ap-page-sub">
            {isAdminPremio ? 'Gerencie os avaliadores da sessão' : `${sessoes.length} sessão(ões) cadastrada(s)`}
          </p>
          </div>
        </div>
        {!isAdminPremio && (
          <div className="ap-header-actions">
            <button className="ap-btn-import" onClick={() => fileInputRef.current.click()} disabled={importando}>
              {importando ? '⏳ Importando...' : '📥 Importar Excel'}
            </button>
            <input type="file" ref={fileInputRef} accept=".xlsx" onChange={handleImportar} style={{display:'none'}} />
            <button className="ap-btn-new" onClick={() => setShowForm(true)}>+ Nova Sessão</button>
          </div>
        )}
      </div>

      <div className="ap-filter-panel">
        <div className="ap-category-tabs" role="tablist" aria-label="Categorias de sessões">
          {Object.entries(CATEGORIA_LABEL).map(([chave, label]) => (
            <button
              key={chave}
              type="button"
              role="tab"
              aria-selected={categoriaAtiva === chave}
              className={categoriaAtiva === chave ? 'is-active' : ''}
              onClick={() => { setCategoriaAtiva(chave); setSearch(''); }}
            >
              <span>{label}</span>
              <strong>{sessoes.filter(s => s.categoria === chave).length}</strong>
            </button>
          ))}
        </div>
        {diasDaCategoria.length > 0 && (
          <div className="ap-day-tabs" role="tablist" aria-label="Dias das sessões">
            {diasDaCategoria.map(dia => (
              <button
                key={dia}
                type="button"
                role="tab"
                aria-selected={diaAtivo === dia}
                className={diaAtivo === dia ? 'is-active' : ''}
                onClick={() => setDiaAtivo(dia)}
              >
                <span>{new Date(`${dia}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')}</span>
                <strong>{new Date(`${dia}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</strong>
                <small>{sessoesDaCategoria.filter(s => s.dia === dia).length} sessão(ões)</small>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="ap-search">
        <span>🔍</span>
        <input placeholder="Buscar sessão por nome ou tema…" value={search}
          onChange={e => setSearch(e.target.value)} />
        {search && <button onClick={() => setSearch('')}>✕</button>}
      </div>

      {showForm && (
        <div className="ap-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="ap-modal" onClick={e => e.stopPropagation()}>
            <div className="ap-modal-header">
              <h2>Nova Sessão</h2>
              <button className="ap-modal-close" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="ap-form">
              <div className="ap-form-row">
                <div className="ap-field">
                  <label>Nome da Sessão *</label>
                  <input name="nome" value={formData.nome} onChange={handleChange} placeholder="Ex: TLL-1" required autoFocus />
                </div>
                <div className="ap-field">
                  <label>Tema *</label>
                  <input name="tema" value={formData.tema} onChange={handleChange} placeholder="Ex: Trauma" required />
                </div>
              </div>
              <div className="ap-form-row">
                <div className="ap-field">
                  <label>Tipo</label>
                  <input name="tipo" value={formData.tipo} onChange={handleChange} required />
                </div>
                <div className="ap-field">
                  <label>Categoria (avaliação/premiação) *</label>
                  <select name="categoria" value={formData.categoria} onChange={handleChange} required>
                    <option value="temas_livres">📋 Temas Livres</option>
                    <option value="posteres">🖼️ Pôsteres</option>
                    <option value="temas_correlatos">🎤 Temas Correlatos</option>
                  </select>
                </div>
              </div>
              <div className="ap-form-row ap-form-row-3">
                <div className="ap-field">
                  <label>Data *</label>
                  <input name="dia" type="date" value={formData.dia} onChange={handleChange} required />
                </div>
                <div className="ap-field">
                  <label>Início *</label>
                  <input name="horario_inicio" type="time" data-format="HH:mm" value={formData.horario_inicio} onChange={handleChange} required />
                </div>
                <div className="ap-field">
                  <label>Fim *</label>
                  <input name="horario_fim" type="time" data-format="HH:mm" value={formData.horario_fim} onChange={handleChange} required />
                </div>
              </div>
<div className="ap-form-actions">
                <button type="submit" className="ap-btn-primary">✓ Criar Sessão</button>
                <button type="button" className="ap-btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="ap-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="ap-modal ap-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="ap-modal-header ap-modal-danger">
              <h2>⚠️ Confirmar Exclusão</h2>
              <button className="ap-modal-close" onClick={() => setConfirmDelete(null)}>✕</button>
            </div>
            <p className="ap-modal-desc">
              Apagar <strong>{confirmDelete.nome}: {confirmDelete.tema}</strong>?<br />
              <span className="ap-warn">Todas as apresentações e avaliações serão perdidas.</span>
            </p>
            <div className="ap-form-actions ap-form-actions-pad">
              <button className="ap-btn-danger" onClick={() => handleDelete(confirmDelete)}>🗑 Apagar</button>
              <button className="ap-btn-ghost" onClick={() => setConfirmDelete(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {Object.keys(grouped).sort().length === 0 ? (
        <div className="ap-empty">
          {sessoes.length === 0 ? 'Nenhuma sessão cadastrada. Crie a primeira!' : 'Nenhuma sessão encontrada para esta categoria e dia.'}
        </div>
      ) : (
        Object.keys(grouped).sort().map(dia => (
          <div key={dia} className="ap-day-group">
            <div className="ap-day-label">{formatDate(dia)}</div>
            <div className="ap-sessao-list">
              {grouped[dia].map(sessao => {
                const totalApresentacoes = sessao.apresentacoes?.length || 0;
                const avaliacoesAbertas = (sessao.apresentacoes || []).filter(ap => ap.avaliacao_aberta).length;
                const statusAbertura = totalApresentacoes === 0
                  ? { classe: 'is-empty', texto: 'Sem apresentações' }
                  : avaliacoesAbertas === 0
                    ? { classe: 'is-closed', texto: 'Avaliações fechadas' }
                    : avaliacoesAbertas === totalApresentacoes
                      ? { classe: 'is-open', texto: 'Todas abertas' }
                      : { classe: 'is-partial', texto: `${avaliacoesAbertas}/${totalApresentacoes} abertas` };
                return (
                <div key={sessao.id} className="ap-sessao-card">
                  <div className="ap-sessao-main">
                    <div className="ap-sessao-info">
                      <span className="ap-sessao-nome">{sessao.nome}</span>
                      <span className="ap-sessao-premio-badge">{CATEGORIA_LABEL[sessao.categoria] || sessao.categoria}</span>
                      <span className="ap-sessao-sep">·</span>
                      <span className="ap-sessao-tema">{sessao.tema}</span>
                    </div>
                    <div className="ap-sessao-meta">
                      <span className="ap-sessao-tipo-tag">{sessao.tipo}</span>
                      <span className="ap-sessao-horario">🕐 {sessao.horario_inicio.substring(0,5)}–{sessao.horario_fim.substring(0,5)}</span>
                      <span className="ap-sessao-count">📋 {sessao.apresentacoes?.length || 0} apres.</span>
                      <span className={`ap-sessao-open-status ${statusAbertura.classe}`}>
                        <i aria-hidden="true" /> {statusAbertura.texto}
                      </span>
                    </div>
                    <div className="ap-sessao-avals-row">
                      {sessao.categoria !== 'posteres' && sessao.avaliadores_detalhes?.length > 0 && (
                        <span className="ap-sessao-avals">
                          👥 {sessao.avaliadores_detalhes.map(a => a.first_name || a.username).join(', ')}
                        </span>
                      )}
                      {sessao.categoria === 'posteres' && (
                        <span className="ap-sessao-avals">
                          👥 {(sessao.totens_avaliadores || []).map(g => `T${g.totem}: ${g.avaliadores.length}`).join(' · ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ap-sessao-actions">
                    <Link to={`/admin-panel/sessao/${sessao.id}?returnCategoria=${categoriaAtiva}&returnDia=${diaAtivo}`} className="ap-btn-action ap-btn-detail">
                      {isAdminPremio ? '👥 Gerenciar Avaliadores' : 'Ver Apresentações'}
                    </Link>
                    {!isAdminPremio && (
                      <>
                        <button className="ap-btn-action ap-btn-edit" onClick={() => setEditingSessao(sessao)}>✏️ Editar</button>
                        <button className="ap-btn-action ap-btn-del" onClick={() => setConfirmDelete(sessao)}>🗑</button>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {editingSessao && (
        <SessaoEditModal
          sessao={editingSessao}
          onClose={() => setEditingSessao(null)}
          onSuccess={() => { fetchSessoes(); setEditingSessao(null); }}
        />
      )}
    </div>
  );
}

export default AdminPanel;
